import React, { useState, useEffect } from 'react';
import { X, Plus, Loader } from 'lucide-react';
import { Document, Goal, Task, db } from '../../lib/db';
import { toast } from 'sonner';

interface AddTaskModalProps {
  document: Document;
  dbUserId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AddTaskModal({ document, dbUserId, onClose, onSuccess }: AddTaskModalProps) {
  const [loading, setLoading] = useState(false);
  const [deliverables, setDeliverables] = useState<Goal[]>([]);
  const [selectedDeliverableId, setSelectedDeliverableId] = useState<string>('');
  const [taskTitle, setTaskTitle] = useState('');
  const [taskDescription, setTaskDescription] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState<'low' | 'medium' | 'high'>('medium');

  useEffect(() => {
    loadDeliverables();
  }, []);

  const loadDeliverables = async () => {
    try {
      const goals = await db.goals.findByUserId(dbUserId);
      setDeliverables(goals.filter(g => !g.is_archived));
    } catch (error) {
      console.error('Error loading deliverables:', error);
      toast.error('Failed to load deliverables');
    }
  };

  const handleSubmit = async () => {
    if (!selectedDeliverableId || !taskTitle) {
      toast.error('Please select a deliverable and enter a task title');
      return;
    }

    setLoading(true);
    try {
      // Create the task
      const task = await db.tasks.create({
        goal_id: selectedDeliverableId,
        user_id: dbUserId,
        title: taskTitle,
        description: taskDescription || `Created from document: ${document.filename}`,
        due_date: taskDueDate ? new Date(taskDueDate) : undefined,
        priority: taskPriority,
        is_completed: false,
        order_index: 0
      });

      // Record the action
      await db.documentActions.create({
        document_id: document.id,
        user_id: dbUserId,
        action_type: 'add_task',
        action_data: { 
          task_id: task.id, 
          deliverable_id: selectedDeliverableId,
          task_title: taskTitle
        }
      });

      toast.success('Task added successfully');
      onSuccess();
    } catch (error) {
      console.error('Error adding task:', error);
      toast.error('Failed to add task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Add Task to Deliverable</h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Deliverable *
                </label>
                <select
                  value={selectedDeliverableId}
                  onChange={(e) => setSelectedDeliverableId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select a deliverable...</option>
                  {deliverables.map(deliverable => (
                    <option key={deliverable.id} value={deliverable.id}>
                      {deliverable.title} ({deliverable.year})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Task Title *
                </label>
                <input
                  type="text"
                  value={taskTitle}
                  onChange={(e) => setTaskTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter task title..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Enter task description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Due Date (Optional)
                  </label>
                  <input
                    type="date"
                    value={taskDueDate}
                    onChange={(e) => setTaskDueDate(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Priority
                  </label>
                  <select
                    value={taskPriority}
                    onChange={(e) => setTaskPriority(e.target.value as 'low' | 'medium' | 'high')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !selectedDeliverableId || !taskTitle}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin inline mr-2" />
                    Adding...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 inline mr-2" />
                    Add Task
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}